import { useEffect, useMemo, useState } from 'react';
import { api, formatErr } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PageHeader, Stat, PageShell } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid, Cell } from 'recharts';
import { Play, Square, PhoneCall, UserCheck, Video, Award, TrendingUp, CalendarDays, Building2, Trophy } from 'lucide-react';
import TeamActivity from '@/components/TeamActivity';

const RANGES = [
  { key: 'today', label: 'Today', days: 0 },
  { key: '7d', label: '7 days', days: 6 },
  { key: '15d', label: '15 days', days: 14 },
  { key: '30d', label: '30 days', days: 29 },
  { key: 'mtd', label: 'Month to date', mtd: true },
  { key: '3m', label: '3 months', days: 89 },
  { key: '6m', label: '6 months', days: 179 },
];

function rangeToDates(rangeKey) {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const to = iso(today);
  const r = RANGES.find(x => x.key === rangeKey) || RANGES[4];
  if (r.mtd) {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: iso(first), to };
  }
  const d = new Date(today); d.setDate(d.getDate() - r.days);
  return { from: iso(d), to };
}

export default function Dashboard() {
  const { user } = useAuth();
  const canFilter = ['admin', 'manager'].includes(user?.role);
  const [range, setRange] = useState('mtd');
  const [userId, setUserId] = useState('all');
  const [users, setUsers] = useState([]);
  const [overview, setOverview] = useState(null);
  const [attToday, setAttToday] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [joinings, setJoinings] = useState(null);
  const [upcoming, setUpcoming] = useState(null);

  const { from, to } = useMemo(() => rangeToDates(range), [range]);

  const load = async () => {
    try {
      const params = { from_date: from, to_date: to };
      if (canFilter && userId !== 'all') params.user_id = userId;
      const [ov, at, lb, jn, up] = await Promise.all([
        api.get('/analytics/overview', { params }),
        api.get('/attendance/today'),
        api.get('/analytics/leaderboard', { params: { from_date: from, to_date: to } }),
        api.get('/analytics/joinings', { params: { from_date: from, to_date: to } }),
        api.get('/analytics/upcoming-joinings'),
      ]);
      setOverview(ov.data); setAttToday(at.data);
      setLeaderboard(lb.data.slice(0, 5)); setJoinings(jn.data); setUpcoming(up.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range, userId]);
  useEffect(() => { if (canFilter) api.get('/users').then(({ data }) => setUsers(data)).catch(() => {}); }, [canFilter]);

  const doPunch = async (kind) => {
    try {
      await api.post(`/attendance/${kind}`, {});
      toast.success(kind === 'punch-in' ? 'Punched in. Have a great day!' : 'Punched out. Well done.');
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const totalsToday = overview?.totals_today || {};
  const totalsRange = overview?.totals_range || {};
  const chartColors = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))', 'hsl(var(--muted-foreground))', '#8B5CF6', '#10B981', '#F59E0B', '#3B82F6', '#EC4899'];

  const rangeLabel = RANGES.find(r => r.key === range)?.label || range;

  return (
    <PageShell>
      <PageHeader
        eyebrow={`${new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}`}
        title={`Namaste, ${user?.name?.split(' ')[0] || 'there'}.`}
        subtitle="Your SHRS command centre — filter by date range and recruiter to slice the team's performance instantly."
        actions={
          user?.role === 'hr' ? (
            attToday?.punch_out ? (
              <Badge variant="secondary" data-testid="punch-status">Punched out · {attToday.hours}h</Badge>
            ) : attToday?.punch_in ? (
              <Button data-testid="punch-out-btn" onClick={() => doPunch('punch-out')} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
                <Square className="w-4 h-4 mr-2" /> Punch Out
              </Button>
            ) : (
              <Button data-testid="punch-in-btn" onClick={() => doPunch('punch-in')} className="bg-primary hover:bg-primary/90 text-white">
                <Play className="w-4 h-4 mr-2" /> Punch In
              </Button>
            )
          ) : null
        }
      />

      {/* Filter row */}
      <div className="tactical-card p-3 mb-6 flex flex-wrap items-center gap-3">
        <CalendarDays className="w-4 h-4 text-muted-foreground ml-1" />
        <div className="flex flex-wrap items-center gap-1.5" data-testid="range-chips">
          {RANGES.map(r => (
            <button key={r.key} data-testid={`range-${r.key}`} onClick={() => setRange(r.key)}
              className={`px-3 py-1.5 rounded-md text-[11px] uppercase tracking-widest border transition-colors ${range === r.key ? 'bg-primary text-white border-primary' : 'border-border/70 hover:border-primary text-muted-foreground'}`}>
              {r.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          {canFilter && (
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger className="w-56 h-9" data-testid="recruiter-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All recruiters</SelectItem>
                {users.filter(u => u.role !== 'admin').map(u => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <span className="hidden md:inline">{from} → {to}</span>
        </div>
      </div>

      {/* KPI Row — range totals */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <Stat testid="kpi-joinings" label={`Joinings · ${rangeLabel}`} value={totalsRange.joinings ?? 0} hint="Candidates who joined" accent />
        <Stat testid="kpi-offers" label={`Offers · ${rangeLabel}`} value={totalsRange.offers_rolled ?? 0} hint="Rolled out" />
        <Stat testid="kpi-lineups" label={`Lineups · ${rangeLabel}`} value={totalsRange.lineups ?? 0} hint="Candidates lined up" />
        <Stat testid="kpi-interviews" label={`Interviews · ${rangeLabel}`} value={totalsRange.interviews_processed ?? 0} hint="Processed" />
        <Stat testid="kpi-calls" label={`Calls · ${rangeLabel}`} value={totalsRange.calls_made ?? 0} hint="Total calls" />
      </div>

      {/* Team Activity — admin/manager only */}
      {canFilter && <TeamActivity />}

      {/* Upcoming joinings this week */}
      {upcoming && (
        <div className="tactical-card p-6 mb-8 bg-gradient-to-br from-primary/5 to-transparent border-primary/30">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">This week · Mon → Sat</div>
              <div className="font-display text-2xl font-black tracking-tight">Upcoming Joinings</div>
              <div className="text-xs text-muted-foreground mt-1">{upcoming.week_start} → {upcoming.week_end}</div>
            </div>
            <Trophy className="w-5 h-5 text-primary" />
          </div>
          {upcoming.items.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="upcoming-grid">
              {upcoming.items.map((j, i) => (
                <div key={i} className="tactical-card p-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    {new Date(j.expected_date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </div>
                  <div className="font-display text-base font-black mt-1 truncate">{j.candidate_name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">→ {j.company}</div>
                  <div className="text-[10px] uppercase tracking-widest text-primary mt-2">by {j.recruiter}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No joinings expected this week yet. Ask recruiters to fill "upcoming joinings" in their daily report.</div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        {/* Trend */}
        <div className="lg:col-span-2 tactical-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{rangeLabel}</div>
              <div className="font-display text-2xl font-black tracking-tight">Activity Trend</div>
            </div>
            <TrendingUp className="w-5 h-5 text-primary" />
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={overview?.trend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(d) => d?.slice(5)} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
              <Line type="monotone" dataKey="calls_made" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 3 }} name="Calls" />
              <Line type="monotone" dataKey="lineups" stroke="hsl(var(--secondary))" strokeWidth={2} dot={{ r: 3 }} name="Lineups" />
              <Line type="monotone" dataKey="interviews_processed" stroke="hsl(var(--chart-4))" strokeWidth={2} dot={{ r: 3 }} name="Interviews" />
              <Line type="monotone" dataKey="joinings" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3 }} name="Joinings" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Ops summary + Today totals */}
        <div className="space-y-4">
          <div className="tactical-card p-5 bg-[hsl(var(--sidebar-bg))] text-white border-transparent">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">Today snapshot</div>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <div className="font-display text-3xl font-black text-primary">{totalsToday.calls_made || 0}</div>
                <div className="text-xs text-zinc-400">Calls today</div>
              </div>
              <div>
                <div className="font-display text-3xl font-black">{totalsToday.lineups || 0}</div>
                <div className="text-xs text-zinc-400">Lineups today</div>
              </div>
              <div>
                <div className="font-display text-3xl font-black text-emerald-400">{totalsToday.joinings || 0}</div>
                <div className="text-xs text-zinc-400">Joinings today</div>
              </div>
              <div>
                <div className="font-display text-3xl font-black">{overview?.punched_in_today ?? 0}</div>
                <div className="text-xs text-zinc-400">Punched in</div>
              </div>
            </div>
          </div>
          <div className="tactical-card p-5">
            <div className="flex items-center gap-2 mb-2"><Award className="w-4 h-4 text-primary" /><div className="text-[11px] uppercase tracking-widest text-muted-foreground">Top by joinings · {rangeLabel}</div></div>
            {leaderboard[0] ? (
              <>
                <div className="font-display text-2xl font-black">{leaderboard[0].user_name}</div>
                <div className="text-sm text-muted-foreground mt-1">{leaderboard[0].joinings} joinings · {leaderboard[0].offers_rolled} offers</div>
              </>
            ) : <div className="text-sm text-muted-foreground">No data yet.</div>}
          </div>
        </div>
      </div>

      {/* Joinings by client + leaderboard mini */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <div className="tactical-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{rangeLabel}</div>
              <div className="font-display text-2xl font-black tracking-tight">Joinings by Client</div>
            </div>
            <Building2 className="w-5 h-5 text-primary" />
          </div>
          {(joinings?.by_client || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={joinings.by_client}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="client" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {joinings.by_client.map((_, i) => <Cell key={i} fill={chartColors[i % chartColors.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-sm text-muted-foreground py-8 text-center">No joinings recorded yet. Move a candidate to "joined" status to see them here.</div>
          )}
        </div>

        <div className="tactical-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Ranked by joinings</div>
              <div className="font-display text-2xl font-black tracking-tight">Team Leaderboard</div>
            </div>
            <UserCheck className="w-5 h-5 text-primary" />
          </div>
          <div className="space-y-2" data-testid="leaderboard-mini">
            {leaderboard.map((r, i) => (
              <div key={r.user_id} className={`flex items-center gap-4 p-3 rounded-md border transition-colors ${i === 0 ? 'border-primary bg-primary/5' : 'border-border/70 hover:border-primary/50'}`}>
                <div className={`font-display text-2xl font-black w-8 ${i === 0 ? 'text-primary' : 'text-muted-foreground'}`}>{String(i + 1).padStart(2, '0')}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{r.user_name}</div>
                  <div className="text-xs text-muted-foreground">{r.offers_rolled} offers · {r.lineups} lineups</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-xl font-black text-emerald-500">{r.joinings}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">joinings</div>
                </div>
              </div>
            ))}
            {leaderboard.length === 0 && <div className="text-sm text-muted-foreground">No reports yet.</div>}
          </div>
        </div>
      </div>

      {/* Portal breakdown */}
      <div className="tactical-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Where the calls came from</div>
            <div className="font-display text-2xl font-black tracking-tight">Portal Breakdown</div>
          </div>
          <PhoneCall className="w-5 h-5 text-primary" />
        </div>
        {(overview?.portals || []).length > 0 ? (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={overview.portals}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="portal" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
              <Bar dataKey="calls" radius={[6, 6, 0, 0]}>
                {overview.portals.map((_, i) => <Cell key={i} fill={chartColors[i % chartColors.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-sm text-muted-foreground py-8 text-center">No portal data yet.</div>
        )}
      </div>
    </PageShell>
  );
}
